import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const GoogleAdsSetupGuide = ({ onBack }) => {
  const steps = [
    {
      number: 1,
      title: 'Go to Google Ads',
      description: 'Visit ads.google.com and log into your Google Ads account.',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/ri9vanhe_Goals%20-%3E%20Summary%20and%20Click%20create%20converison.png'
    },
    {
      number: 2,
      title: 'Navigate to Conversions',
      description: 'In the left sidebar, click Goals, then Conversions, then Summary. Click "New conversion action".',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/nz7jeuwa_Click%20Create%20Conversion%20for%20website.png'
    },
    {
      number: 3,
      title: 'Enter your website URL',
      description: 'Select "Conversions on a website" and enter your website URL (e.g., bookingflows.com). Select the Google tag option.',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/zihpfku2_Enter%20Bookingflows.com.png'
    },
    {
      number: 4,
      title: 'Select "Purchase" category',
      description: 'Choose the "Purchase" category — this tracks when someone completes a booking checkout.',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/vuap413w_Select%20for%20Purchase%20events.png'
    },
    {
      number: 5,
      title: 'Create the conversion',
      description: 'Click "+ Create conversion" under the Purchase category.',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/h5tk5b2v_Create%20Conversion.png'
    },
    {
      number: 6,
      title: 'Choose "Manually with code"',
      description: 'Select "Manually with code" and click Done, then "Save and continue".',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/qqk1gx2u_Should%20look%20like%20this.png'
    },
    {
      number: 7,
      title: 'Select manual tag setup',
      description: 'Choose "Manually with code" to install the conversion tag. Click Done.',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/kb0pbm9p_Select%20your%20tag%20and%20then%20manual%20with%20code.png'
    },
    {
      number: 8,
      title: 'Copy your Conversion ID & Label',
      description: 'Select the "Use Google Tag Manager" tab. You\'ll see your Conversion ID (starts with AW-) and Conversion Label. Copy both values — you\'ll paste these into BookingFlows.',
      image: 'https://customer-assets.emergentagent.com/job_d2f91f5a-1ecb-4aee-9397-6a29982ddf9c/artifacts/yzins03s_Press%20Next%20and%20then%20select%20use%20google%20tag%20manager%20and%20you%20will%20see%20your%20Conversion%20ID%20and%20Conversion%20Label.png'
    },
    {
      number: 9,
      title: 'Save on both sides',
      description: 'Click Save on Google\'s page to finish setup. Then go back to BookingFlows, paste your Conversion ID and Label, and click Save. You\'re all set — conversions will now track automatically when customers book.',
      image: null
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-6 text-gray-600 hover:text-gray-900 -ml-2"
          data-testid="back-to-conversions"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Conversions
        </Button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Google Ads Conversion Setup</h1>
        <p className="text-gray-500 mb-8">Follow these steps to connect your Google Ads account. Takes about 2 minutes.</p>

        <div className="space-y-8">
          {steps.map((step) => (
            <div key={step.number} className="bg-white rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {step.number}
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{step.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                  </div>
                </div>
              </div>
              {step.image && (
                <div className="border-t border-gray-100">
                  <img 
                    src={step.image} 
                    alt={`Step ${step.number}: ${step.title}`}
                    className="w-full"
                    loading="lazy"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100 text-center">
          <p className="text-sm text-gray-700">
            Need help? Check <a href="https://support.google.com/google-ads/answer/6095821" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium hover:underline">Google's official guide</a> or contact us.
          </p>
        </div>

        <div className="mt-6 text-center">
          <Button onClick={onBack} className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6">
            Back to Conversions Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoogleAdsSetupGuide;
